import { inject, Service } from '@angular/core';
import { TranslateParser, TranslateService } from '@ngx-translate/core';
import { pick, pickBy, toPairs } from 'lodash';
import { Observable, Subject, switchMap } from 'rxjs';
import { NotificationService } from '@core/providers/notification/notification.service';
import { VenueCategoryDetails } from '@shared/interfaces/analytics.interface';
import { SharedComponentViewType } from '@shared/interfaces/common.interface';
import { I18nAvailableLanguage } from '@shared/interfaces/i18n.interface';
import {
  MissingTranslationsForLanguages,
  Statement,
  StatementGroup,
  StatementPopupType,
  StatementTranslation,
  StatementTranslationFromBackend,
  StatementsFromBackend,
} from '@shared/interfaces/statements.interface';

@Service()
export class FoodbackQuestionsService {
  private notificationService = inject(NotificationService);
  private translateService = inject(TranslateService);
  private translateParser = inject(TranslateParser);

  // large part of this code were created long long time ago, when things were named in a different way
  // so now: group => category, statement => question

  private addQuestionSubject: Subject<void> = new Subject<void>();
  private configureCategoriesSubject: Subject<void> = new Subject<void>();

  mapBackendResponseToQuestionGroups(backendResponse: StatementsFromBackend): StatementGroup[] {
    return Object.keys(backendResponse).map(groupName => {
      const group = backendResponse[groupName];
      const statements: Statement[] = group.map(groupEntry => ({
        ...groupEntry,
        translations: this.getTranslationsFromQuestionGroupEntry(groupEntry.translations),
      }));

      return {
        groupName,
        statements,
        groupUuid: group[0] && group[0].groupUuid, // group uuid is not sent at the top level, so we extract it from the first question
      };
    });
  }

  pickSpecificCategoriesFromStatementsResponse(
    statements: StatementsFromBackend,
    categories: VenueCategoryDetails[]
  ): StatementsFromBackend {
    const categoriesNames = categories.map(category => category.name);

    return pick(statements, categoriesNames);
  }

  getTranslationsFromQuestionGroupEntry(
    translations: StatementTranslationFromBackend[] | null | undefined
  ): StatementTranslation {
    if (!translations) {
      return {};
    }

    return translations.reduce<StatementTranslation>((reduced, translation) => {
      reduced[translation.lang] = translation.value;

      return reduced;
    }, {});
  }

  getTranslationsForAccountStatementsForm(
    availableLanguages: I18nAvailableLanguage[],
    translations: StatementTranslation
  ): StatementTranslation {
    const emptyTranslations = availableLanguages.reduce<StatementTranslation>((reduced, lang) => {
      reduced[lang.code] = '';

      return reduced;
    }, {});
    const suppliedTranslations = pickBy(translations, (_value, key) =>
      availableLanguages.find(availableLanguage => availableLanguage.code === key)
    );

    return { ...emptyTranslations, ...suppliedTranslations };
  }

  getTranslationsAsKeyValuePairs(
    translations: StatementTranslation
  ): StatementTranslationFromBackend[] {
    return toPairs(translations)
      .map(([lang, value]: [string, string]) => ({ lang, value }))
      .filter((entry: StatementTranslationFromBackend) => entry.value);
  }

  addQuestion(): void {
    this.addQuestionSubject.next();
  }

  configureCategories(): void {
    this.configureCategoriesSubject.next();
  }

  onAddQuestion(): Observable<void> {
    return this.addQuestionSubject.asObservable();
  }

  onConfigureCategories(): Observable<void> {
    return this.configureCategoriesSubject.asObservable();
  }

  displayActivationTogglingConfirmationPopup$(
    viewType: SharedComponentViewType,
    popupType: StatementPopupType,
    newValue: any,
    childrenNameTranslation: string
  ): Observable<boolean> {
    let translateKey: string;

    switch (popupType) {
      case StatementPopupType.ENFORCEMENT:
        translateKey = newValue
          ? 'SHARED.FOODBACK_STATEMENTS.MAIN.STATE_CHANGE_MODALS.ACCOUNT.ENFORCE'
          : 'SHARED.FOODBACK_STATEMENTS.MAIN.STATE_CHANGE_MODALS.ACCOUNT.STOP_ENFORCING';

        break;
      case StatementPopupType.ACTIVATION:
        translateKey = this.getTranslateKeyForEnablingConfirmation(viewType, newValue);
        break;
    }
    return this.translateService.get(translateKey).pipe(
      switchMap(
        (DATA: { TITLE: string; TEXT: string; BUTTONS: { OK: string; CANCEL?: string } }) => {
          const { TITLE, TEXT, BUTTONS } = DATA;
          const interpolatedTitle: string = this.translateService.instant(TITLE);
          const interpolatedText: string = this.translateService
            .instant(TEXT)
            .replaceAll('{{ childrenName }}', childrenNameTranslation);

          return this.notificationService.displayConfirmationPopup(
            interpolatedTitle,
            interpolatedText,
            BUTTONS
          );
        }
      )
    );
  }

  displayStatusChangeResultPopup(
    viewType: SharedComponentViewType,
    popupType: StatementPopupType,
    newValue: any
  ): void {
    let translateKey: string;

    switch (popupType) {
      case StatementPopupType.ENFORCEMENT:
        translateKey = newValue
          ? 'SHARED.FOODBACK_STATEMENTS.MAIN.STATE_CHANGE_MODALS.ACCOUNT.ENFORCE_RESULT'
          : 'SHARED.FOODBACK_STATEMENTS.MAIN.STATE_CHANGE_MODALS.ACCOUNT.STOP_ENFORCING_RESULT';
        break;
      case StatementPopupType.ACTIVATION:
        translateKey = this.getTranslateKeyForEnablingResult(viewType, newValue);
        break;
    }

    this.notificationService.confirmWithKey(translateKey);
  }

  showMissingTranslationsNotification(
    missingTranslationsForLanguages: MissingTranslationsForLanguages
  ): void {
    FoodbackQuestionsService.missingTranslationCodes(missingTranslationsForLanguages).forEach(
      (translationCode: string) => {
        const language: string = this.translateService.instant(
          `SHARED.LANGUAGES.${translationCode.toUpperCase()}`
        );
        const info: string = this.translateService.instant(
          'ACCOUNT.FOODBACK.NOTIFICATIONS.MISSING_TRANSLATION.TEXT',
          { language }
        );

        this.notificationService.warn(info);
      }
    );
  }

  static missingTranslationCodes(missing: MissingTranslationsForLanguages): string[] {
    return Array.isArray(missing) ? missing : Object.values(missing ?? {});
  }

  displayEditConfirmationPopup$(): Observable<boolean> {
    return this.notificationService.displayConfirmationDialogWithTranslationKey(
      'ACCOUNT.FOODBACK.NOTIFICATIONS.EDIT_CONFIRMATION_SUCCESS'
    );
  }

  displayDeleteConfirmationPopup$(): Observable<boolean> {
    return this.notificationService.displayConfirmationPopupWithTranslationKey(
      'ACCOUNT.FOODBACK.NOTIFICATIONS.DELETE_CONFIRMATION_SUCCESS'
    );
  }

  private getTranslateKeyForEnablingConfirmation(
    viewType: SharedComponentViewType,
    isEnabled: boolean
  ): string {
    switch (viewType) {
      case SharedComponentViewType.ACCOUNT:
        return isEnabled
          ? 'SHARED.FOODBACK_STATEMENTS.MAIN.STATE_CHANGE_MODALS.ACCOUNT.ACTIVATE'
          : 'SHARED.FOODBACK_STATEMENTS.MAIN.STATE_CHANGE_MODALS.ACCOUNT.DEACTIVATE';
      case SharedComponentViewType.VENUE:
        return isEnabled
          ? 'SHARED.FOODBACK_STATEMENTS.MAIN.STATE_CHANGE_MODALS.VENUE.ACTIVATE'
          : 'SHARED.FOODBACK_STATEMENTS.MAIN.STATE_CHANGE_MODALS.VENUE.DEACTIVATE';
    }
  }

  private getTranslateKeyForEnablingResult(
    viewType: SharedComponentViewType,
    isEnabled: boolean
  ): string {
    switch (viewType) {
      case SharedComponentViewType.ACCOUNT:
        return isEnabled
          ? 'SHARED.NOTIFICATIONS.ACTIVATE_RESULT_SUCCESS'
          : 'SHARED.NOTIFICATIONS.DEACTIVATE_RESULT_SUCCESS';
      case SharedComponentViewType.VENUE:
        return isEnabled
          ? 'SHARED.NOTIFICATIONS.ACTIVATE_RESULT_SUCCESS'
          : 'SHARED.NOTIFICATIONS.DEACTIVATE_RESULT_SUCCESS';
    }
  }
}
